import { useEffect, useId, useRef } from "react";

/**
 * Modal confirmation for destructive actions (revoke a device, delete a passkey). Focus starts on
 * Cancel so Enter never confirms by accident; Escape and the backdrop cancel.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busy = false,
  className = "",
  onConfirm,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  busy?: boolean;
  className?: string;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const id = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onCancel]);
  return (
    <div
      className="confirm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={className ? `confirm ${className}` : "confirm"}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-t`}
        aria-describedby={`${id}-d`}
      >
        <h2 className="confirm-title" id={`${id}-t`}>
          {title}
        </h2>
        <div className="confirm-body" id={`${id}-d`}>
          {children}
        </div>
        <div className="confirm-actions">
          <button ref={cancelRef} type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
